import React from 'react';
import { LAYER_SELECTOR, isTextField } from 'Utils/playAlong/tapInput';

/**
 * The tutorial's control areas while taps play: Next and Keep practicing (the
 * step buttons) and the video's bottom-right controls, the volume panel and
 * Pause included.
 */
export const TUTORIAL_CONTROL_AREAS = '#tutorial-step-buttons, [data-tutorial-controls]';

const inControlArea = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(TUTORIAL_CONTROL_AREAS) !== null;

/**
 * A panel a control opens in place (the volume panel). Keyboard focus may
 * stay in it while it is open.
 */
export const CONTROL_PANEL = '[data-control-panel]';

/** Hand focus back to the page if a control area still holds it. */
const returnFocusToPage = () => {
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && inControlArea(focused)) focused.blur();
};

/**
 * After a keyboard press: keep focus on a control that now has its popup open
 * (`aria-expanded="true"`) or that sits inside an open panel, so the focus ring
 * and a screen reader's place are kept; otherwise hand it back.
 */
const returnKeyboardFocusToPage = () => {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement)) return;
  if (focused.getAttribute('aria-expanded') === 'true') return;
  if (focused.closest(CONTROL_PANEL)) return;
  returnFocusToPage();
};

/** A click made by Space / Enter on a button reads `detail` 0; a pointer's is 1 or more. */
const isKeyboardClick = (event: Event): boolean =>
  event.type === 'click' && (event as MouseEvent).detail === 0;

/**
 * Focus a pointer left anywhere else on the tutorial page (a chapter in the
 * chapters list, the input picker's row, a control a closing dialog hands
 * focus back to after a tap or click in it) goes back to the page too. Not
 * focus in the exercise area (a tap there is a note), in a text field, or in
 * an open dialog or menu.
 */
const leftByPointer = (
  focused: Element | null,
  pointerFocused: EventTarget | null,
  exerciseArea: HTMLElement | null,
): focused is HTMLElement =>
  focused instanceof HTMLElement &&
  focused !== document.body &&
  focused === pointerFocused &&
  !exerciseArea?.contains(focused) &&
  !isTextField(focused) &&
  focused.closest(LAYER_SELECTOR) === null;

/**
 * While taps play, Space and Enter are taps, except on a control the player
 * has moved to with Tab. So once a control in the tutorial's control areas
 * has been USED, focus goes back to the page and the next Space is a tap:
 *   - pressed by any input (a click, which Space / Enter on a button also
 *     makes, or the release of Space / Enter on a control that acts on
 *     keydown);
 *   - a pointer press or drag ended on it (a slider's thumb);
 *   - a panel closing takes its focused element with it, and Escape that
 *     closes it hands back the control that opened it.
 * Focus moved with Tab is left where it is, so a keyboard player can still
 * reach a control and press it. A keyboard press that opens a panel, or one
 * made inside an open panel, keeps focus there until the panel closes. The hand-back runs after the event, so the
 * control's own action (a keyup click, a ripple) is not cut short.
 *
 * Anywhere else on the page outside the exercise area, focus that came with a
 * pointer goes back to the page as well: when the tap exercise comes on screen
 * (a chapter clicked in the chapters list keeps focus while its exercise
 * loads), and after any pointer use while it is on screen. Focus that came
 * from the keyboard is never moved there.
 */
export function useReturnFocusAfterControlUse(
  active: boolean,
  exerciseArea?: React.RefObject<HTMLElement | null>,
): void {
  // The element that last took focus, if a pointer press put it there (rather
  // than Tab, a key, or a dialog closed with Escape). Noted at all times, so
  // focus a pointer left before the exercise came on screen is known.
  const pointerFocused = React.useRef<EventTarget | null>(null);
  const pointerDown = React.useRef(false);
  // Hand-backs run just after the event. They are not cancelled when taps stop
  // (see below), only when the page goes away.
  const pendingHandBacks = React.useRef(new Set<ReturnType<typeof setTimeout>>());
  const afterTheEvent = React.useCallback((handBack: () => void) => {
    const handle = setTimeout(() => {
      pendingHandBacks.current.delete(handle);
      handBack();
    }, 0);
    pendingHandBacks.current.add(handle);
  }, []);
  React.useEffect(() => {
    const pending = pendingHandBacks.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);
  React.useEffect(() => {
    let lastInputWasPointer = false;
    const onPointerDown = () => {
      lastInputWasPointer = true;
      pointerDown.current = true;
    };
    const onPointerEnd = () => {
      pointerDown.current = false;
    };
    const onKeyDown = () => {
      lastInputWasPointer = false;
    };
    const onFocusIn = (event: FocusEvent) => {
      pointerFocused.current = lastInputWasPointer ? event.target : null;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointerup', onPointerEnd, true);
    document.addEventListener('pointercancel', onPointerEnd, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointerup', onPointerEnd, true);
      document.removeEventListener('pointercancel', onPointerEnd, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, []);

  React.useEffect(() => {
    if (!active) return undefined;
    const returnPointerFocusToPage = () => {
      const focused = document.activeElement;
      if (leftByPointer(focused, pointerFocused.current, exerciseArea?.current ?? null)) {
        focused.blur();
      }
    };
    returnPointerFocusToPage();
    const afterPointerUse = () => afterTheEvent(returnPointerFocusToPage);
    // Focus handed to a control without a press of its own (a dialog that
    // closes puts it back on the control that opened it). Mid-press, the
    // release does it instead, so a drag is not cut short.
    const afterFocusMoves = () => {
      if (!pointerDown.current) afterTheEvent(returnPointerFocusToPage);
    };
    document.addEventListener('pointerup', afterPointerUse, true);
    document.addEventListener('click', afterPointerUse, true);
    document.addEventListener('focusin', afterFocusMoves, true);
    return () => {
      document.removeEventListener('pointerup', afterPointerUse, true);
      document.removeEventListener('click', afterPointerUse, true);
      document.removeEventListener('focusin', afterFocusMoves, true);
    };
  }, [active, exerciseArea, afterTheEvent]);

  React.useEffect(() => {
    if (!active) return undefined;
    // Not cancelled when taps stop: pressing Next on an objective's last
    // pass ends the listening in the same moment, and Next can come back into
    // view at the next exercise still holding focus.
    const afterThisEvent = (event: Event) => {
      if (!inControlArea(event.target)) return;
      afterTheEvent(isKeyboardClick(event) ? returnKeyboardFocusToPage : returnFocusToPage);
    };
    // Space / Enter press a control; Escape closes its panel (and the control
    // that opened it is then done with).
    const afterPressKey = (event: KeyboardEvent) => {
      if (![' ', 'Enter', 'Escape'].includes(event.key)) return;
      if (inControlArea(event.target)) afterTheEvent(returnKeyboardFocusToPage);
    };
    // Capture phase: noted before the app handles the event, so a press that
    // stops the listening (and removes these) is still noted.
    document.addEventListener('click', afterThisEvent, true);
    document.addEventListener('pointerup', afterThisEvent, true);
    document.addEventListener('keyup', afterPressKey, true);
    return () => {
      document.removeEventListener('click', afterThisEvent, true);
      document.removeEventListener('pointerup', afterThisEvent, true);
      document.removeEventListener('keyup', afterPressKey, true);
    };
  }, [active, afterTheEvent]);
}
